// frontend/src/hooks/useEquipmentCatalog.ts
import { useMemo } from 'react';
import {
  CATEGORIES,
  SUBCATEGORIES,
  BRANDS,
  MODELS,
} from '@/data/equipmentCatalogData';
import type { Category, Subcategory, Brand, Model } from '@/types/equipmentCatalog';

/**
 * Returns all equipment categories, ordered by the order field.
 * Categories are static, so this returns a memoized value.
 */
export function useCategories(): Category[] {
  return useMemo(() => {
    return [...CATEGORIES].sort((a, b) => a.order - b.order);
  }, []);
}

/**
 * Returns subcategories for a given category, ordered by the order field.
 * @param categoryId - The category ID to filter by, or undefined to return empty array
 */
export function useSubcategories(categoryId: string | undefined): Subcategory[] {
  return useMemo(() => {
    if (!categoryId) return [];
    return SUBCATEGORIES
      .filter((sub) => sub.categoryId === categoryId)
      .sort((a, b) => a.order - b.order);
  }, [categoryId]);
}

/**
 * Returns brands that have models in a given subcategory.
 * Brands are derived by looking at which models exist for the subcategory.
 * @param subcategoryId - The subcategory ID to filter by, or undefined to return empty array
 */
export function useBrandsForSubcategory(subcategoryId: string | undefined): Brand[] {
  return useMemo(() => {
    if (!subcategoryId) return [];

    // Find all unique brand IDs that have models in this subcategory
    const brandIds = new Set(
      MODELS
        .filter((model) => model.subcategoryId === subcategoryId)
        .map((model) => model.brandId)
    );

    // Get the brand objects and sort alphabetically
    return BRANDS
      .filter((brand) => brandIds.has(brand.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subcategoryId]);
}

/**
 * Returns models for a brand within a subcategory.
 * @param brandId - The brand ID to filter by, or undefined to return empty array
 * @param subcategoryId - The subcategory ID to filter by, or undefined to return empty array
 */
export function useModelsForBrand(
  brandId: string | undefined,
  subcategoryId: string | undefined
): Model[] {
  return useMemo(() => {
    if (!brandId || !subcategoryId) return [];

    return MODELS
      .filter((model) => model.brandId === brandId && model.subcategoryId === subcategoryId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandId, subcategoryId]);
}
